import React, {useEffect, useState} from "react";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {Col, Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import {deepEqual} from "../../../../core/services/utils/utils";
import Dialog from "../../base/dialog";
import PhoneInput from "../../base/input/phone-input";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {MuiRFF, Yup} from '../../form';

const formNames = {
    firstName: 'firstName',
    lastName: 'lastName',
    email: 'email',
    phone: 'phone',
}

const schema = Yup.object().shape({
    [formNames.firstName]: Yup.string().trim().nullable().required(ValidateMessages.required),
    [formNames.lastName]: Yup.string().trim().nullable().required(ValidateMessages.required),
    [formNames.email]: Yup.string().email(ValidateMessages.incorrect).trim().nullable().required(ValidateMessages.required),
    [formNames.phone]: Yup.string().trim().nullable()
        .required(ValidateMessages.required)
        .phoneNo(ValidateMessages.incorrectType("phone"), 10),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const UpsertDriverDialog = ({open, setOpen, selectedDriver, searchCallback}) => {
    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const isMounted = useIsMounted();

    /**
     * Listens to the changes of selectedDriver in the props:
     * With each change, sets the [initialValues] in the state
     */
    useEffect(() => {
        setInitialValues({
            [formNames.firstName]: selectedDriver?.firstname ?? undefined,
            [formNames.lastName]: selectedDriver?.lastname ?? undefined,
            [formNames.email]: selectedDriver?.email ?? undefined,
            [formNames.phone]: selectedDriver?.phone ?? undefined,
        })
    }, [selectedDriver])

    /**
     * creates a new driver or updates the [selectedDriver] by calling the api regarding this action
     */
    const onSubmit = async (values) => {
        setLoading(true);
        if (!!selectedDriver) {
            await _updateTheSelectedDriver(values);
            setOpen(false);
            return;
        }
        await _createNewDriver(values)
        setOpen(false);
    }

    /**
     * Updates the selected driver by calling the api regarding this action
     * @param values
     * @return {Promise<void>}
     * @private
     */
    const _updateTheSelectedDriver = async (values) => {
        const forApi = {
            id: selectedDriver.id,
            firstname: values.firstName,
            lastname: values.lastName,
            phone: values.phone,
            email: values.email,
            updatedProperties: [
                "Firstname",
                "Lastname",
                "Email",
                "Phone"
            ]
        };
        const response = await Api.updateDriverInformation(forApi);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            searchCallback()
        }
    }

    /**
     * Creates a new driver in the system by calling the api regarding this action
     * @param values
     * @return {Promise<void>}
     * @private
     */
    const _createNewDriver = async (values) => {
        const forApi = {
            firstname: values.firstName,
            lastName: values.lastName,
            phone: values.phone,
            email: values.email,
        };
        const response = await Api.createNewDriver(forApi);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            searchCallback();
        }
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={setOpen}
                maxWidth={'sm'}
            >
                <Row className='no-gutters align-items-center w-100 transition'>
                    <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                        <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                            {selectedDriver !== null
                                ? 'Edit Selected Driver'
                                : 'Add a New Driver'
                            }
                        </p>
                    </Row>
                    <Divider className='divider'/>
                    <div className='my-3 w-100'/>
                    <Form
                        className='w-100'
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                        render={({initialValues: formInitialValues, values, form}) => {
                            return (
                                <Row className='no-gutters justify-content-around w-100'>
                                    <Row className='no-gutters justify-content-around w-85'>
                                        <Col xs={12} className={'mb-3'}>
                                            <OutlinedInputForm
                                                label={'First Name'}

                                                name={formNames.firstName}
                                                type={'text'}
                                                required={required[formNames.firstName]}
                                                value={values[formNames.firstName] ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                            />
                                        </Col>
                                        <Col xs={12} className={'mb-3'}>
                                            <OutlinedInputForm
                                                label={'Last Name'}

                                                name={formNames.lastName}
                                                type={'text'}
                                                required={required[formNames.lastName]}
                                                value={values[formNames.lastName] ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                            />
                                        </Col>
                                        <Col xs={12} className={'mb-3'}>
                                            <OutlinedInputForm
                                                label={'Email'}

                                                name={formNames.email}
                                                type={'email'}
                                                required={required[formNames.email]}
                                                value={values[formNames.email] ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                            />
                                        </Col>
                                        <Col xs={12} className={'mb-3'}>
                                            <PhoneInput
                                                form={form}
                                                label={'Phone Number'}
                                                aria-label={'Phone Number of the driver'}
                                                name={formNames.phone}
                                                required={true}
                                                value={values[formNames.phone] ?? ''}
                                            />
                                        </Col>
                                    </Row>
                                    <div className='my-3 w-100'/>
                                    <Divider className='divider'/>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            disabled={!open || loading}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => setOpen(false)}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            {loading ? 'loading...' : 'Close'}
                                        </Button>
                                        <Button
                                            disabled={
                                                !open ||
                                                loading ||
                                                (initialValues !== null && deepEqual(formInitialValues, values))
                                            }
                                            variant={'dialog'}
                                            color={'primary'}
                                            type={'submit'}
                                            borderRadius={'8px 0 8px 0'}
                                        >
                                            {loading ? 'loading...' : selectedDriver !== null ? 'Update' : 'Register'}
                                        </Button>
                                    </Row>
                                </Row>
                            );
                        }}
                    />
                </Row>
            </Dialog>
        </>
    );
}

export default UpsertDriverDialog;
